import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Dialog } from "primereact/dialog"
import { useClearDeviceLogsMutation } from "../api/deviceDiagnosticsApiSlice"
import { relativeTime } from "utility/TimeUtility"

const SEVERITY_CONFIG = [
    { key: "DEBUG", bgClass: "bg-primary", textClass: "" },
    { key: "INFO",  bgClass: "bg-light",   textClass: "text-dark" },
    { key: "WARN",  bgClass: "bg-warning", textClass: "text-dark" },
    { key: "ERROR", bgClass: "bg-danger",  textClass: "" }
]

function DeviceTypeIcon({ type }) {
    switch (type) {
        case "WATCH":  return <i className="fa-solid fa-clock" />
        case "DEVICE": return <i className="fa-solid fa-tablet-screen-button" />
        default:       return null
    }
}

export default function DiagnosticCard({ resume }) {
    const [t] = useTranslation(["global", "device"])
    const navigate = useNavigate()
    const [showClearDialog, setShowClearDialog] = useState(false)
    const [clearLogs, { isLoading: isClearing }] = useClearDeviceLogsMutation()

    const { device, counter, lastLogTimestamp } = resume

    const totalLogs = SEVERITY_CONFIG.reduce((sum, s) => sum + (counter[s.key] ?? 0), 0)

    const handleDetails = () => {
        navigate("/dashboard/devices/diagnostics/logs", { state: { deviceGuid: device.guid } })
    }

    const handleClearConfirm = async () => {
        await clearLogs(device.guid)
        setShowClearDialog(false)
    }

    return (
        <div className="card bg-dark text-white h-100">
            <div className="card-body d-flex flex-column">
                {/* Header */}
                <div className="d-flex align-items-center gap-2 mb-1">
                    <DeviceTypeIcon type={device.type} />
                    <span className="fw-semibold">{device.model}</span>
                    <small className="text-muted ms-auto font-monospace">
                        …{device.guid.substr(device.guid.length - 5)}
                    </small>
                </div>
                <small className="text-muted mb-3">
                    {t(`device:entity.device.type.${device.type ?? "N/A"}`)}
                </small>

                {/* Log counters */}
                <div className="d-flex flex-wrap gap-1 mb-3">
                    {SEVERITY_CONFIG.map(({ key, bgClass, textClass }) => (
                        counter[key]==null ? "" :
                        <span key={key} className={`badge ${bgClass} ${textClass}`}>
                            {key} {counter[key] ?? 0}
                        </span>
                    ))}
                </div>

                {/* Last log */}
                <small className="text-muted mb-3">
                    <i className="fa-regular fa-clock me-1" />
                    {t("device:diagnostics.last_log")}: {relativeTime(lastLogTimestamp)}
                </small>

                {/* Actions */}
                <div className="d-flex gap-2 mt-auto">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() =>
                        navigate("/dashboard/devices/dashboard", { state: { highlightDeviceGuid: device.guid } })
                    }>
                        <i className="fa-solid fa-circle-info me-1" />
                        {t("device:diagnostics.go_to_device")}
                    </button>
                    <button className="btn btn-sm btn-outline-info" onClick={handleDetails}>
                        <i className="fa-solid fa-list me-1" />
                        {t("device:diagnostics.details")}
                    </button>
                    <button
                        className="btn btn-sm btn-outline-danger ms-auto"
                        onClick={() => setShowClearDialog(true)}
                        disabled={totalLogs === 0}
                    >
                        <i className="fa-solid fa-trash me-1" />
                        {t("device:diagnostics.clear_logs")}
                    </button>
                </div>
            </div>

            <Dialog
                header={t("device:diagnostics.clear_logs_confirm_title")}
                visible={showClearDialog}
                onHide={() => setShowClearDialog(false)}
            >
                <p>{t("device:diagnostics.clear_logs_confirm_msg", { device: device.model })}</p>
                <div className="text-end mt-3">
                    <button
                        className="btn btn-danger me-2"
                        onClick={handleClearConfirm}
                        disabled={isClearing}
                    >
                        {t("action.confirm")}
                    </button>
                    <button className="btn btn-secondary" onClick={() => setShowClearDialog(false)}>
                        {t("action.cancel")}
                    </button>
                </div>
            </Dialog>
        </div>
    )
}
